import { isEmpty, pickBy } from 'lodash-es';
import { toSectionGroupKey } from 'src/components/Listings/InventoryEventPage/EventVenueMap/components/v2/SectionGroupPanelV2.utils';
import {
  Listing,
  RowInfo,
  SectionInfo,
  SectionSectionGroup,
  SelectedSectionSettings,
} from 'src/WebApiController';

export const sortRowsByOrdinal = (rows: RowInfo[]) =>
  rows
    .filter(({ ordinal, isSpeculative }) => ordinal != null && !isSpeculative)
    .toSorted((a, b) => a.ordinal! - b.ordinal!);

export const onSectionSelctionWithMyRowDefaults = (
  compListingSettings: SelectedSectionSettings | null,
  selectedSections: SectionInfo[],
  venueMapSections: SectionInfo[],
  listing?: Listing
): SelectedSectionSettings => {
  let rowIdFilter = compListingSettings?.rowIdFilter ?? [];
  let sectionRowIdFilter = compListingSettings?.sectionRowIdFilter ?? {};

  const sectionIdFilter = selectedSections.map((s) => s.id);

  const rowOffset = null;
  // Happy case when no row id filters on all sections, default to `My Row` (rowOffset = 0)
  if (isEmpty(sectionRowIdFilter) || isEmpty(rowIdFilter) || rowOffset === 0) {
    // Set rowOffset to null when no section selected
    const rowOffset = isEmpty(sectionIdFilter) ? null : 0;
    return {
      ...compListingSettings,
      sectionIdFilter,
      sectionRowIdFilter: {},
      rowIdFilter: [],
      rowOffset,
    } as SelectedSectionSettings;
  }

  // Important sanitize SectionRowIdFilter and RowIdFilter to be aligned with sectionid filters
  sectionRowIdFilter = pickBy(
    Object.assign({}, sectionRowIdFilter ?? {}),
    (_, key) => sectionIdFilter.includes(parseInt(key))
  );
  const selectedSectionRowIds = selectedSections.flatMap(({ rows }) =>
    rows.map(({ id }) => id)
  );
  rowIdFilter = rowIdFilter.filter((id) => selectedSectionRowIds.includes(id));

  // Bulk or group settings with existing row id filters, only update the sectionIdFilter
  // which will count in all the rows in the section
  if (!listing) {
    return {
      ...compListingSettings,
      sectionIdFilter,
      sectionRowIdFilter,
      rowIdFilter,
      rowOffset: null,
    } as SelectedSectionSettings;
  }

  const listingSection = venueMapSections.find(
    ({ id }) => id === listing.seating.sectionId
  );
  const sectionsCanBeDefault = selectedSections.filter(
    ({ id }) => sectionRowIdFilter[id] == null
  );
  // No matching listing section => this should not happen
  // Or all sections have row id fitlers => don't default with `My Row`
  if (!listingSection || !sectionsCanBeDefault?.length) {
    return {
      ...compListingSettings,
      sectionRowIdFilter,
      sectionIdFilter,
      rowIdFilter,
      rowOffset: null,
    } as SelectedSectionSettings;
  }

  const sortedSourceRows = sortRowsByOrdinal(listingSection.rows);
  // the ordinal ranking position of the selected row against all the rows
  const rankedOrdinalIndex = sortedSourceRows.findIndex(
    ({ id }) => listing.seating.rowId === id
  );

  for (const section of sectionsCanBeDefault) {
    // Remove all the row filters associated with the section id
    const sectionRowIds = section.rows.map((r) => r.id);
    rowIdFilter = [
      ...(rowIdFilter ?? []).filter((rowId) => !sectionRowIds.includes(rowId)),
    ];

    // get matched row from id or name
    const matchingRow = section.rows.find(
      ({ id, name }) =>
        id === listing.seating.rowId ||
        name?.toLowerCase() === (listing.seating.row ?? '').toLowerCase()
    );

    if (matchingRow !== undefined) {
      // Only store the highest ordinal row id to display in the pill
      sectionRowIdFilter[section.id] = matchingRow.id.toString();

      // Exclude all rows from section if the matching row has no ordinal
      if (matchingRow.ordinal != null) {
        // Push all the row ids with an ordinal less than or equal to the matching row
        section.rows.forEach((row) => {
          if (row.ordinal == null) return;
          if (row.ordinal <= matchingRow.ordinal!) {
            rowIdFilter.push(row.id);
          }
        });
      }
      continue;
    }

    // if no matching row, use row with closest lower oridinal
    if (rankedOrdinalIndex != null) {
      // row with closest low oridinal rank
      const sortedRows = sortRowsByOrdinal(section.rows);
      const filteredRows = sortedRows.filter(
        (_, index) => index <= rankedOrdinalIndex
      );

      if (filteredRows && filteredRows.length > 0) {
        const lastRow = filteredRows[filteredRows.length - 1];
        sectionRowIdFilter[section.id] = lastRow.id.toString();
        filteredRows.forEach((row) => rowIdFilter.push(row.id));
      }
      continue;
    }

    delete sectionRowIdFilter[section.id];
  }

  return {
    ...compListingSettings,
    sectionIdFilter,
    sectionRowIdFilter,
    rowIdFilter,
    rowOffset: null,
  } as SelectedSectionSettings;
};

/**
 * Computes the zone map section defaults for a listing group by finding the union
 * of sections from all individual listings in the group. Each listing's section is
 * looked up in the zone map, and all sections in the same zone group are included.
 */
export const getZoneMapSectionDefaultsForListingGroup = (
  listings: Listing[],
  sectionGroupsBySectionId:
    | Record<string, SectionSectionGroup>
    | null
    | undefined,
  venueMapSections: SectionInfo[]
): SelectedSectionSettings | null => {
  if (!sectionGroupsBySectionId || listings.length === 0) {
    return null;
  }

  // For each listing, find the zone group key, then find all section IDs in that zone group
  const sectionIdsInGroup = new Set<number>();

  for (const listing of listings) {
    const matchedSection = venueMapSections.find((s) =>
      s.rows.some((r) => r.id === listing.seating?.rowId)
    );
    const listingSectionId = matchedSection?.id;
    if (!listingSectionId) continue;

    const listingSectionGroup =
      sectionGroupsBySectionId[listingSectionId.toString()];
    if (!listingSectionGroup) continue;

    // Find the zone group key for this listing's section
    const listingGroupKey = toSectionGroupKey(listingSectionGroup);

    // Find all sections in the same zone group
    for (const [sectionId, ssg] of Object.entries(sectionGroupsBySectionId)) {
      const ssgKey = toSectionGroupKey(ssg);
      if (ssgKey === listingGroupKey) {
        sectionIdsInGroup.add(parseInt(sectionId));
      }
    }
  }

  if (sectionIdsInGroup.size === 0) {
    return null;
  }

  const sectionIdFilter = Array.from(sectionIdsInGroup);

  // Get all row IDs from the selected sections in the venue map
  const selectedSections = venueMapSections.filter((s) =>
    sectionIdFilter.includes(s.id)
  );
  const rowIdFilter = selectedSections.flatMap((s) => s.rows.map((r) => r.id));

  return {
    sectionIdFilter,
    rowIdFilter,
    sectionRowIdFilter: {},
    sectionIdFilterFromZoneMap: false,
    includeBetterZones: false,
    rowOffset: null,
    sectionFilterMode: null,
  } as SelectedSectionSettings;
};
